import {
    BuildingOfficeIcon,
    UsersIcon,
    ChartBarIcon,
} from "@heroicons/react/24/solid";
import { ChevronDownIcon } from "@heroicons/react/24/solid";
import { getProject } from "interfaces/project";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { StatCard } from "components/ui/Card";
import { Button } from "components/ui/Button";

const countries = [
    { code: "US", name: "United States" },
    { code: "CA", name: "Canada" },
    { code: "MX", name: "Mexico" },
    { code: "FR", name: "France" },
    { code: "JP", name: "Japan" },
    { code: "NG", name: "Nigeria" },
    { code: "BR", name: "Brazil" },
    { code: "IN", name: "India" },
    { code: "DE", name: "Germany" },
    { code: "GB", name: "United Kingdom" },
];

const sectors = [
    "E-commerce",
    "SaaS / Technology",
    "Professional Services",
    "Health and Wellness",
    "Education",
    "Finance",
    "Real Estate",
    "Food and Beverage",
    "Fashion and Beauty",
    "Travel and Tourism",
    "Automotive",
    "Other",
];

const ageRanges = [
    "18-24 years",
    "25-34 years",
    "35-44 years",
    "45-54 years",
    "55-64 years",
    "65+ years",
];

const cspCategories = [
    "Executives and higher intellectual professions",
    "Intermediate professions",
    "Employees",
    "Workers",
    "Craftsmen, merchants and business owners",
    "Farmers",
    "Retirees",
    "Students",
];

const genders = ["Male", "Female", "Non-binary", "All genders"];

const getFlagEmoji = (countryCode: string) =>
    countryCode
        .toUpperCase()
        .replace(/./g, (char) =>
            String.fromCodePoint(127397 + char.charCodeAt(0)),
        );

const Breadcrumbs = ({
    items,
}: {
    items: { label: string; href: string }[];
}) => (
    <nav className="text-sm text-ink-muted mb-4">
        {items.map((item, index) => (
            <span key={index}>
                <a href={item.href} className="hover:text-accent transition-colors duration-150">
                    {item.label}
                </a>
                {index < items.length - 1 && " / "}
            </span>
        ))}
    </nav>
);

const inputClasses = "mt-2 block w-full rounded-lg border border-border bg-surface px-3 py-1.5 text-ink placeholder:text-ink-faint focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:border-accent sm:text-sm";
const selectClasses = "block w-full appearance-none rounded-lg border border-border bg-surface py-1.5 pl-3 pr-10 text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:border-accent sm:text-sm";
const labelClasses = "block text-sm font-medium text-ink";

export default function Account() {
    const params = useParams();
    const [searchConsoleData, _setSearchConsoleData] = useState({
        totalClicks: 0,
        totalImpressions: 0,
        avgCTR: 0,
        avgPosition: 0,
    });

    const [project, setProject] = useState(null);

    const initProject = async () => {
        const res = await getProject(params?.id);
        console.log(res);
        setProject(res);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        initProject();
    }, []);

    return (
        <div className="min-h-screen bg-canvas font-sans">
            <Breadcrumbs items={[{ label: "Account", href: "/account" }]} />
            <div className="mt-3 px-5 max-w-7xl mx-auto">
                <div className="space-y-12">
                    {/* Company Information Section */}
                    <fieldset className="border-b border-border pb-12">
                        <legend className="text-base font-semibold text-ink flex items-center gap-2">
                            <BuildingOfficeIcon className="h-5 w-5 text-accent" />
                            Project Information
                        </legend>
                        <p className="mt-1 text-sm text-ink-muted">
                            Enter your project details for better brand
                            identification.
                        </p>

                        <div className="mt-10 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
                            <div className="sm:col-span-3">
                                <label
                                    htmlFor="company-name"
                                    className={labelClasses}
                                >
                                    Project Name
                                </label>
                                <input
                                    type="text"
                                    id="company-name"
                                    name="company-name"
                                    value={project?.projectId}
                                    className={inputClasses}
                                    placeholder="My Company LLC"
                                />
                            </div>

                            <div className="sm:col-span-3">
                                <label
                                    htmlFor="company-location"
                                    className={labelClasses}
                                >
                                    Project Location
                                </label>
                                <div className="relative mt-2">
                                    <select
                                        id="company-location"
                                        name="company-location"
                                        onSelect={(e) => {
                                            console.log(e);
                                        }}
                                        className={selectClasses}
                                    >
                                        <option value="">Select...</option>
                                        {countries.map(({ code, name }) => (
                                            <option key={code} value={code}>
                                                {getFlagEmoji(code)} {name}
                                            </option>
                                        ))}
                                    </select>
                                    <ChevronDownIcon className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 h-5 w-5 text-ink-faint" />
                                </div>
                            </div>

                            <div className="sm:col-span-4">
                                <label
                                    htmlFor="website-url"
                                    className={labelClasses}
                                >
                                    Website URL
                                </label>
                                <input
                                    type="url"
                                    id="website-url"
                                    name="website-url"
                                    className={inputClasses}
                                    placeholder="https://www.mywebsite.com"
                                />
                            </div>

                            <div className="sm:col-span-3">
                                <label
                                    htmlFor="business-sector"
                                    className={labelClasses}
                                >
                                    Business Sector
                                </label>
                                <div className="relative mt-2">
                                    <select
                                        id="business-sector"
                                        name="business-sector"
                                        className={selectClasses}
                                    >
                                        <option value="">Select...</option>
                                        {sectors.map((sector) => (
                                            <option key={sector} value={sector}>
                                                {sector}
                                            </option>
                                        ))}
                                    </select>
                                    <ChevronDownIcon className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 h-5 w-5 text-ink-faint" />
                                </div>
                            </div>

                            <div className="col-span-full">
                                <label
                                    htmlFor="company-offer"
                                    className={labelClasses}
                                >
                                    Products / Services Offered
                                </label>
                                <div className="mt-2">
                                    <textarea
                                        id="company-offer"
                                        name="company-offer"
                                        rows={3}
                                        className={inputClasses.replace("mt-2 ", "")}
                                        placeholder="Describe your main products or services..."
                                    />
                                </div>
                            </div>
                        </div>
                    </fieldset>

                    {/* Customer Persona / Target Section */}
                    <fieldset className="mt-8 border-b border-border pb-6">
                        <legend className="text-base font-semibold text-ink flex items-center gap-2">
                            <UsersIcon className="h-5 w-5 text-accent" />
                            Customer Persona / Target
                        </legend>
                        <p className="mt-1 text-sm text-ink-muted">
                            Define your ideal customer to better target your
                            marketing efforts.
                        </p>

                        <div className="mt-10 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
                            <div className="sm:col-span-3">
                                <label
                                    htmlFor="target-gender"
                                    className={labelClasses}
                                >
                                    Target Gender
                                </label>
                                <div className="relative mt-2">
                                    <select
                                        id="target-gender"
                                        name="target-gender"
                                        className={selectClasses}
                                    >
                                        <option value="">Select...</option>
                                        {genders.map((gender) => (
                                            <option key={gender} value={gender}>
                                                {gender}
                                            </option>
                                        ))}
                                    </select>
                                    <ChevronDownIcon className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 h-5 w-5 text-ink-faint" />
                                </div>
                            </div>

                            <div className="sm:col-span-3">
                                <label
                                    htmlFor="target-age"
                                    className={labelClasses}
                                >
                                    Target Age Range
                                </label>
                                <div className="relative mt-2">
                                    <select
                                        id="target-age"
                                        name="target-age"
                                        className={selectClasses}
                                    >
                                        <option value="">Select...</option>
                                        {ageRanges.map((age) => (
                                            <option key={age} value={age}>
                                                {age}
                                            </option>
                                        ))}
                                    </select>
                                    <ChevronDownIcon className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 h-5 w-5 text-ink-faint" />
                                </div>
                            </div>

                            <div className="sm:col-span-3">
                                <label
                                    htmlFor="target-csp"
                                    className={labelClasses}
                                >
                                    Socio-Professional Category
                                </label>
                                <div className="relative mt-2">
                                    <select
                                        id="target-csp"
                                        name="target-csp"
                                        className={selectClasses}
                                    >
                                        <option value="">Select...</option>
                                        {cspCategories.map((csp) => (
                                            <option key={csp} value={csp}>
                                                {csp}
                                            </option>
                                        ))}
                                    </select>
                                    <ChevronDownIcon className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 h-5 w-5 text-ink-faint" />
                                </div>
                            </div>

                            <div className="sm:col-span-3">
                                <label
                                    htmlFor="target-location"
                                    className={labelClasses}
                                >
                                    Target Location
                                </label>
                                <div className="relative mt-2">
                                    <select
                                        id="target-location"
                                        name="target-location"
                                        className={selectClasses}
                                    >
                                        <option value="">Select...</option>
                                        {countries.map(({ code, name }) => (
                                            <option key={code} value={code}>
                                                {getFlagEmoji(code)} {name}
                                            </option>
                                        ))}
                                    </select>
                                    <ChevronDownIcon className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 h-5 w-5 text-ink-faint" />
                                </div>
                            </div>

                            <div className="col-span-full">
                                <label
                                    htmlFor="persona-description"
                                    className={labelClasses}
                                >
                                    Persona Description
                                </label>
                                <div className="mt-2">
                                    <textarea
                                        id="persona-description"
                                        name="persona-description"
                                        rows={4}
                                        className={inputClasses.replace("mt-2 ", "")}
                                        placeholder="Describe your ideal customer: their habits, needs, motivations..."
                                    />
                                </div>
                            </div>
                        </div>
                    </fieldset>

                    {/* Search Console Section */}
                    <fieldset className="border-b border-border pb-12">
                        <legend className="text-base font-semibold text-ink flex items-center gap-2">
                            <ChartBarIcon className="h-5 w-5 text-accent" />
                            Search Console
                        </legend>
                        <p className="mt-1 text-sm text-ink-muted">
                            Connect and track your website's performance in
                            search engines.
                        </p>

                        <div className="mt-10 space-y-8">
                            {/* Main Metrics */}
                            <div className="grid grid-cols-1 gap-6 sm:grid-cols-4">
                                <StatCard label="Total Clicks" value={searchConsoleData.totalClicks.toLocaleString()} />
                                <StatCard label="Impressions" value={searchConsoleData.totalImpressions.toLocaleString()} />
                                <StatCard label="Average CTR" value={`${searchConsoleData.avgCTR}%`} />
                                <StatCard label="Average Position" value={searchConsoleData.avgPosition} />
                            </div>

                            {/* Configuration */}
                            <div className="grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
                                <div className="sm:col-span-4">
                                    <label
                                        htmlFor="search-console-property"
                                        className={labelClasses}
                                    >
                                        Search Console Property
                                    </label>
                                    <input
                                        type="url"
                                        id="search-console-property"
                                        name="search-console-property"
                                        className={inputClasses}
                                        placeholder="https://www.mywebsite.com"
                                    />
                                </div>

                                <div className="sm:col-span-2">
                                    <label className="block text-sm font-medium text-ink mb-2">
                                        Connection Status
                                    </label>
                                    <Button className="w-full">
                                        Connect to Google
                                    </Button>
                                </div>

                                <div className="col-span-full">
                                    <label
                                        htmlFor="tracking-keywords"
                                        className={labelClasses}
                                    >
                                        Keywords to Track
                                    </label>
                                    <div className="mt-2">
                                        <textarea
                                            id="tracking-keywords"
                                            name="tracking-keywords"
                                            rows={3}
                                            className={inputClasses.replace("mt-2 ", "")}
                                            placeholder="Separate keywords with commas (e.g. digital marketing, SEO, search optimization)"
                                        />
                                    </div>
                                    <p className="mt-2 text-sm text-ink-muted">
                                        List the main keywords you want to track
                                        performance for.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </fieldset>

                    {/* Action Buttons */}
                    <div className="mt-6 flex items-center justify-end gap-x-6">
                        <button
                            type="button"
                            className="text-sm font-semibold text-ink hover:text-ink-muted transition-colors duration-150"
                        >
                            Cancel
                        </button>
                        <Button type="submit" variant="accent">
                            Save Changes
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
}
